import { useCallback, useEffect, useRef, useState } from 'react';

export interface AppVersionInfo {
  buildId: string;
  title: string;
  notes: string[];
}

/** Previous releases stored the running build here. It survived reloads, so the notice came back after refresh. */
const LEGACY_KNOWN_BUILD_ID_KEY = 'app_build_id_v1';
const DISMISSED_BUILD_ID_KEY = 'app_version_dismissed_v1';
const POLL_INTERVAL_MS = 60_000;

export interface VersionEvaluation {
  loadedBuildId: string;
  updateAvailable: boolean;
  versionInfo: AppVersionInfo | null;
}

/**
 * The first check in a document adopts the server build and stays quiet.
 * A reload starts with no loaded build, so a refresh clears the notice.
 * Only a later check in the same document can raise it.
 */
export function evaluateAppVersion(
  info: AppVersionInfo,
  loadedBuildId: string | null,
  dismissedBuildId: string | null,
): VersionEvaluation {
  if (!loadedBuildId || loadedBuildId === info.buildId) {
    return {
      loadedBuildId: info.buildId,
      updateAvailable: false,
      versionInfo: null,
    };
  }

  const updateAvailable = dismissedBuildId !== info.buildId;
  return {
    loadedBuildId,
    updateAvailable,
    versionInfo: updateAvailable ? info : null,
  };
}

function readSessionStorage(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSessionStorage(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Ignore quota / privacy mode errors.
  }
}

function removeSessionStorage(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Ignore privacy mode errors.
  }
}

async function fetchVersionInfo(): Promise<AppVersionInfo | null> {
  const response = await fetch('/version.json', { cache: 'no-store' });
  if (!response.ok) {
    return null;
  }

  const data = (await response.json()) as Partial<AppVersionInfo>;
  if (!data.buildId || typeof data.buildId !== 'string') {
    return null;
  }

  return {
    buildId: data.buildId,
    title: typeof data.title === 'string' ? data.title : 'Update',
    notes: Array.isArray(data.notes)
      ? data.notes.filter((note): note is string => typeof note === 'string')
      : [],
  };
}

export interface UseAppVersionResult {
  updateAvailable: boolean;
  versionInfo: AppVersionInfo | null;
  dismiss: () => void;
  refresh: () => void;
}

export function useAppVersion(): UseAppVersionResult {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [versionInfo, setVersionInfo] = useState<AppVersionInfo | null>(null);
  const loadedBuildIdRef = useRef<string | null>(null);

  const evaluateVersion = useCallback((info: AppVersionInfo) => {
    const result = evaluateAppVersion(
      info,
      loadedBuildIdRef.current,
      readSessionStorage(DISMISSED_BUILD_ID_KEY),
    );
    loadedBuildIdRef.current = result.loadedBuildId;
    setVersionInfo(result.versionInfo);
    setUpdateAvailable(result.updateAvailable);
  }, []);

  const checkForUpdate = useCallback(async () => {
    if (process.env.NODE_ENV !== 'production') {
      return;
    }

    try {
      const info = await fetchVersionInfo();
      if (info) {
        evaluateVersion(info);
      }
    } catch {
      // Ignore transient network errors during polling.
    }
  }, [evaluateVersion]);

  const dismiss = useCallback(() => {
    if (!versionInfo) {
      return;
    }
    writeSessionStorage(DISMISSED_BUILD_ID_KEY, versionInfo.buildId);
    setUpdateAvailable(false);
  }, [versionInfo]);

  const refresh = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      return undefined;
    }

    removeSessionStorage(LEGACY_KNOWN_BUILD_ID_KEY);
    void checkForUpdate();

    const intervalId = window.setInterval(() => {
      void checkForUpdate();
    }, POLL_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdate]);

  return {
    updateAvailable,
    versionInfo,
    dismiss,
    refresh,
  };
}
